/**
 * Pillars - Figma `6377:87682`, drawn at full size in `6373:15817`.
 *
 * A heading over a row of value cards that scrolls sideways inside the content grid,
 * the part past its edge clipped (it ran to the viewport edges until 2026-10-30), with previous/next buttons underneath. The scrolling itself is
 * `shared/Scroller`, which explains why it is a scroll container and not a slider.
 *
 * **The cards are not one size.** The design staggers three - tall, short and square -
 * hung from the top edge, so the row reads as a skyline rather than a strip. Like the
 * colours, the sizes cycle a fixed sequence taken from the design rather than asking an
 * editor for them; with six cards, as in the mock-up, the row is exactly the drawing.
 *
 * **The card colours cycle a fixed palette** taken from the design - grey, green, blue,
 * black, orange - and each one carries its own text colour, a darker step of the same
 * hue. A photograph does not take a colour, so the palette counts coloured cards only:
 * counting every card put blue where the design has green. There is no colour field on
 * `shared.card`; if the designer wants per-card control, that is a field, and a small one.
 *
 * A card with an image is a photograph instead of a coloured panel; `shared.card`
 * gained `image` for that.
 */
import { SectionShell } from '../primitives';
import { Scroller } from '../shared/Scroller';
import type { Card, PillarsSection } from '../../types/sections';
import { Image, SIZES } from '../../components/ui/Image';

/** Backgrounds in the order the design uses them, each with the text colour it is drawn in. */
const PALETTE = [
  { background: 'bg-grey-300', text: 'text-black' },
  { background: 'bg-green-500', text: 'text-green-900' },
  { background: 'bg-blue-200', text: 'text-blue-dark-400' },
  { background: 'bg-black', text: 'text-white' },
  { background: 'bg-orange-200', text: 'text-orange-800' }
];

/**
 * 365x455, 264x370 and 370x370, in the order `6373:15817` places them. `max-w` keeps the
 * widest from overrunning a phone, where the Figma file draws nothing.
 */
const TALL = 'h-[455px] w-[365px]';
const SHORT = 'h-[370px] w-[264px]';
const SQUARE = 'h-[370px] w-[370px]';
const SIZES_IN_ORDER = [TALL, SHORT, TALL, SQUARE, SHORT, TALL];

const CARD = 'relative max-w-[80vw] shrink-0 snap-start self-start overflow-hidden rounded-m';

function PillarCard({ item, index, tone }: { item: Card; index: number; tone: number }) {
  const size = SIZES_IN_ORDER[index % SIZES_IN_ORDER.length];

  if (item.image) {
    return (
      <figure className={`${CARD} ${size} m-0 flex items-end bg-surface-sunken p-6`}>
        <Image
          media={item.image}
          alt={item.image.alternativeText ?? item.title}
          sizes={SIZES.third}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div aria-hidden className="absolute inset-0 bg-gradient-to-b from-transparent from-[34%] to-black/50 to-70%" />
        {item.body ? (
          <figcaption className="relative w-full rounded-s bg-black/50 px-6 py-8 text-body text-white backdrop-blur-[7px]">
            {item.body}
          </figcaption>
        ) : null}
      </figure>
    );
  }

  const colour = PALETTE[tone % PALETTE.length];
  return (
    <article className={`${CARD} ${size} ${colour.background} ${colour.text} flex flex-col justify-between gap-6 p-8`}>
      <h3 className="text-h4">{item.title}</h3>
      {item.body ? <p className="text-body-md">{item.body}</p> : null}
    </article>
  );
}

export default function Pillars({ data }: { data: PillarsSection }) {
  const items = data.items ?? [];

  // `fullBleed` is ignored: pages seeded before 2026-10-30 carry it ticked, and with it the
  // section dropped the content grid altogether. The row stays inside the grid.
  return (
    <SectionShell settings={{ ...data.settings, fullBleed: false }} name="pillars">
      {data.heading ? <h2 className="text-h4 text-ink">{data.heading}</h2> : null}

      {items.length ? (
        <Scroller label={data.heading ?? 'Pillars'}>
          {items.map((item, i) => (
            <PillarCard
              key={i}
              item={item}
              index={i}
              tone={items.slice(0, i).filter((card) => !card.image).length}
            />
          ))}
        </Scroller>
      ) : null}
    </SectionShell>
  );
}
